import type { StreamSnapshot, QueuedMessage } from "./types";
import type { BagTemplate } from "@langchain/langgraph-sdk";

export class SmartStreamStore<
  StateType extends Record<string, unknown> = Record<string, unknown>,
  Bag extends BagTemplate = BagTemplate
> {
  private snapshot: StreamSnapshot<StateType, Bag>;
  private listeners = new Set<() => void>();

  /**
   * Locally-queued messages awaiting drain — entries we appended via the
   * inbox path (`multitaskStrategy: "inbox"`) that haven't yet appeared
   * in the SDK's `messages` array. The renderer interleaves these with
   * `messages` so the user sees their input the moment they hit send,
   * with a "queued" / "delivering" indicator. Each entry is a normal
   * frozen array reference; React subscribers get a new array on every
   * mutation so referential equality drives re-renders.
   */
  private queuedMessages: ReadonlyArray<QueuedMessage> = [];

  constructor(initialSnapshot: StreamSnapshot<StateType, Bag>) {
    this.snapshot = initialSnapshot;
  }

  private notifyScheduled = false;

  /**
   * Called during render to sync the latest useStream return value.
   *
   * Updates the snapshot reference immediately (so getSnapshot returns the
   * latest value), but defers listener notification to a microtask. This
   * avoids the React warning "Cannot update a component while rendering a
   * different component" — which fires when setSnapshot is called during
   * ChatRootWithOwnedStream's render and a listener triggers setState in
   * a subscriber like SubmitButton.
   */
  setSnapshot(next: StreamSnapshot<StateType, Bag>): void {
    if (next === this.snapshot) return;

    this.snapshot = next;
    this.scheduleNotify();
  }

  /**
   * Append a locally-queued message awaiting drain. Returns the same
   * entry the caller passed in so a wrapped submit() can stash a
   * reference and update the `inboxRowId` once the server responds.
   */
  addQueuedMessage(entry: QueuedMessage): QueuedMessage {
    this.queuedMessages = [...this.queuedMessages, entry];
    this.scheduleNotify();
    return entry;
  }

  /**
   * Replace the queued entry with id `localId` — used to attach the
   * server-returned `inboxRowId` once the inbox POST resolves. No-op
   * if the local entry is already gone (e.g. the drain raced ahead
   * and removeQueuedMessagesByInboxRowIds already cleared it).
   */
  updateQueuedMessage(localId: string, patch: Partial<QueuedMessage>): void {
    const idx = this.queuedMessages.findIndex((q) => q.id === localId);
    if (idx === -1) return;
    const updated = { ...this.queuedMessages[idx]!, ...patch };
    this.queuedMessages = [
      ...this.queuedMessages.slice(0, idx),
      updated,
      ...this.queuedMessages.slice(idx + 1),
    ];
    this.scheduleNotify();
  }

  /**
   * Drop queued entries whose `inboxRowId` is in the given set. Called
   * by `useLanggraphStream` whenever the SDK's messages array contains
   * a server-emitted message with `additional_kwargs.inboxRowId` —
   * that's the signal the drain has materialized our optimistic entry
   * as a real message in the transcript.
   */
  removeQueuedMessagesByInboxRowIds(rowIds: ReadonlySet<number>): void {
    if (rowIds.size === 0) return;
    const next = this.queuedMessages.filter(
      (q) => q.inboxRowId === undefined || !rowIds.has(q.inboxRowId)
    );
    if (next.length === this.queuedMessages.length) return;
    this.queuedMessages = next;
    this.scheduleNotify();
  }

  /** Drop a single queued entry by local id (e.g. on inbox POST failure). */
  removeQueuedMessageById(localId: string): void {
    const next = this.queuedMessages.filter((q) => q.id !== localId);
    if (next.length === this.queuedMessages.length) return;
    this.queuedMessages = next;
    this.scheduleNotify();
  }

  /** Read the current queued-messages list (frozen, safe to render). */
  getQueuedMessages = (): ReadonlyArray<QueuedMessage> => {
    return this.queuedMessages;
  };

  /**
   * useSyncExternalStore protocol: subscribe to changes.
   */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * useSyncExternalStore protocol: get current snapshot.
   */
  getSnapshot = (): StreamSnapshot<StateType, Bag> => {
    return this.snapshot;
  };

  private scheduleNotify(): void {
    if (this.notifyScheduled) return;
    this.notifyScheduled = true;
    queueMicrotask(() => {
      this.notifyScheduled = false;
      this.listeners.forEach((listener) => listener());
    });
  }
}
